/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C68 · How a PERSON looks, in one module. Colour, initials, avatar seed.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THERE WERE THREE `initialsOf`S AND THEY DID NOT AGREE.** `people-look.ts`,
 * `people-screen.tsx` and `task-columns.ts` each carried one; two of them returned an empty string
 * for a person with no name and the third returned `?`. So the same member rendered a blank disc
 * on one screen and a `?` on another, and nobody could see it because no screen shows two of them
 * side by side.
 *
 * ⚠️ **The `?` version won.** A blank circle reads as a picture that failed to load — which is the
 * one thing an avatar fallback exists to prevent.
 *
 * ⚠️⚠️ **THIS MODULE IS THE PREREQUISITE FOR THE BLOBATAR, NOT HOUSEKEEPING.** Thirty-odd call
 * sites draw a person today; with the fallback decided in one place, swapping initials for a
 * generated face is a change to one file rather than to thirty.
 */

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE HUE COMES FROM THE MEMBER ID, AND THE FACE FROM THE EMAIL.       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The two seeds are deliberately different, and the reason is the sign-in screen.
 *
 * A member id is stable for life but **unknowable before somebody has signed in** — the login form
 * knows exactly one thing about the person, the address they just typed. So a face seeded on the
 * id could never be drawn on the password step without asking the server *"does this account
 * exist"*, and `sign-in.tsx` refuses to ask: that question is an account-enumeration oracle and
 * the whole flow is built to deny it.
 *
 * Seeding the face on the **email** makes the login blobatar the same one they meet inside, by
 * construction, with no request and no leak — it is a pure function of what they typed, so it
 * renders for every syntactically valid address whether or not an account is behind it. That
 * property is load-bearing: a face that appeared only for real accounts would be the oracle again,
 * drawn in pictures.
 *
 * ⚠️ **The cost is that changing your email rerolls your face**, and it is accepted (23 Sep). The
 * hue does NOT reroll, because it stays on the id — so a person who changes address keeps their
 * colour and only the geometry moves. They stay "the teal one" on every screen.
 */

/** The design system's eight identity hues, chosen by the member id. */
export function identityColor(id: string): string {
  let hash = 0;
  for (const char of id) hash = (hash * 31 + char.charCodeAt(0)) % 997;
  return `var(--id-${String((hash % 8) + 1)})`;
}

/**
 * Two letters for an avatar, from a display name.
 *
 * ⚠️ **`?` for a nameless person, never an empty string.** An empty fallback is a blank circle,
 * and a blank circle is what a failed image looks like.
 */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

/**
 * ⚠️⚠️ **`avatarSeed` LIVES IN `@forecastify/shared` AND IS RE-EXPORTED HERE.** The API sends
 * the seed beside every avatar URL and the sign-in screen computes it from the typed address; one
 * implementation is the only thing that keeps those two strings identical, and identical is the
 * whole promise. This module re-exports it so a screen reaching for *how a person looks* finds
 * everything in one import.
 */
export { avatarSeed } from '@forecastify/shared';
